/**
 * Maison Moxi mega menu photo preview.
 * The preview panel in each text mega menu (snippets/mm-menu-preview.liquid) shows a product photo
 * of that menu's case type (card holder / MagSafe compatible / protective). Photos are prepared ahead:
 * - shortly after page load, every panel loads a few random products of its case type and preloads them;
 * - when a top-level menu opens, every link in it gets its own random product (of the menu's case type)
 *   picked and preloaded.
 * Hovering then swaps instantly, without a visible second switch. Product lists come from the light
 * alternate template templates/collection.mm-preview.liquid (?view=mm-preview) and are cached.
 */
(function () {
  if (window.mmMenuPreviewReady) return;
  window.mmMenuPreviewReady = true;

  const TYPES = {
    'card-holder': 'Phone Case With Card Holder',
    magsafe: 'Magnetic Impact-Resistant Case',
    protective: 'Impact-Resistant Case',
  };
  const PARENT_VIEWS = {
    'phone-case-with-card-holder': 'card-holder',
    'magsafe-case': 'magsafe',
    'protective-phone-cases': 'protective',
  };
  const lists = new Map();
  const loaded = new Map();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function handleOf(href) {
    const m = (href || '').match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function viewOf(href) {
    try {
      return new URL(href || '', window.location.origin).searchParams.get('view') || '';
    } catch (e) {
      return '';
    }
  }

  /** @returns {Promise<Array<{t: string, i: string}>>} */
  function listOf(handle) {
    if (!handle) return Promise.resolve([]);
    if (!lists.has(handle)) {
      lists.set(
        handle,
        fetch(`${root}collections/${handle}?view=mm-preview`)
          .then((r) => (r.ok ? r.text() : '[]'))
          .then((t) => {
            try {
              return JSON.parse(t);
            } catch (e) {
              return [];
            }
          })
          .catch(() => [])
      );
    }
    return lists.get(handle);
  }

  /** Resolves with the url once the image is in the browser cache ('' on error). */
  function preload(url) {
    if (!url) return Promise.resolve('');
    if (!loaded.has(url)) {
      loaded.set(
        url,
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => resolve('');
          img.src = url;
        })
      );
    }
    return loaded.get(url);
  }

  function pick(list, view, count) {
    const want = TYPES[view];
    let pool = want ? list.filter((p) => p.t === want) : list.slice();
    if (!pool.length) pool = list.slice();
    const out = [];
    while (pool.length && out.length < count) {
      out.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0].i);
    }
    return out;
  }

  /** @param {HTMLElement} panel */
  function setPreview(panel, { title, img, url, bg }) {
    const image = panel.querySelector('.mm-menu-preview__img');
    const placeholder = panel.querySelector('.mm-menu-preview__placeholder');
    const titleEl = panel.querySelector('.mm-menu-preview__title');
    const card = panel.querySelector('.mm-menu-preview__card');

    if (titleEl && title !== undefined) titleEl.textContent = title || '';
    if (card && url) card.setAttribute('href', url);
    if (bg !== undefined) panel.style.setProperty('--mm-preview-bg', bg || '');

    if (img) {
      if (image) {
        if (image.getAttribute('src') !== img) image.setAttribute('src', img);
        image.hidden = false;
      }
      if (placeholder) placeholder.hidden = true;
    } else if (img === '') {
      if (image) image.hidden = true;
      if (placeholder) placeholder.hidden = false;
    }
  }

  function panelView(panel) {
    return PARENT_VIEWS[handleOf(panel.dataset.mmDefaultUrl)] || '';
  }

  /** Load a few default photos for a panel and show one of them right away (menu still closed). */
  function preparePanel(panel) {
    if (panel.mmDefaults) return panel.mmDefaults;
    panel.mmDefaults = listOf(handleOf(panel.dataset.mmDefaultUrl))
      .then((list) => Promise.all(pick(list, panelView(panel), 4).map(preload)))
      .then((urls) => {
        const ok = urls.filter(Boolean);
        panel.mmImgs = ok;
        if (ok.length && (!panel.dataset.mmToken || panel.dataset.mmToken === 'default')) setPreview(panel, { img: ok[0] });
        return ok;
      });
    return panel.mmDefaults;
  }

  /** Pick and preload a photo for one link (view comes from the link or from the menu). */
  function prepareLink(link, panel) {
    if (link.mmReady) return link.mmReady;
    const href = link.getAttribute('href') || '';
    const view = viewOf(href) || panelView(panel);
    link.mmReady = listOf(handleOf(href))
      .then((list) => preload(pick(list, view, 1)[0] || link.dataset.mmPreviewImg || ''))
      .then((url) => {
        link.mmImg = url;
        return url;
      });
    return link.mmReady;
  }

  function prepareMenu(panel) {
    preparePanel(panel);
    const list = panel.closest('.mega-menu__list');
    if (!list || list.mmPrepared) return;
    list.mmPrepared = true;
    list.querySelectorAll('[data-mm-preview]').forEach((link) => {
      if (link instanceof HTMLElement) prepareLink(link, panel);
    });
  }

  /** @param {HTMLElement} panel */
  function resetPreview(panel) {
    prepareMenu(panel);
    // Moving over the top-level link fires this repeatedly; only pick a new photo once per opening.
    if (panel.dataset.mmToken === 'default') return;
    panel.dataset.mmToken = 'default';
    const imgs = panel.mmImgs || [];
    setPreview(panel, {
      title: panel.dataset.mmDefaultTitle,
      url: panel.dataset.mmDefaultUrl,
      bg: '',
      img: imgs.length ? imgs[Math.floor(Math.random() * imgs.length)] : undefined,
    });
  }

  /** @param {Event} event */
  function onEnter(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const topLink = target.closest('.menu-list__list-item');
    const link = target.closest('[data-mm-preview]');

    if (!link) {
      if (topLink && target.closest('.menu-list__link')) {
        topLink.querySelectorAll('.mm-menu-preview').forEach((panel) => {
          if (panel instanceof HTMLElement) resetPreview(panel);
        });
      }
      return;
    }

    const list = link.closest('.mega-menu__list');
    const panel = list && list.querySelector('.mm-menu-preview');
    if (!(panel instanceof HTMLElement) || !(link instanceof HTMLElement)) return;

    const href = link.getAttribute('href') || '';
    const token = 'link:' + href;
    if (panel.dataset.mmToken === token) return;
    panel.dataset.mmToken = token;

    const styles = getComputedStyle(link);
    const swatch = styles.getPropertyValue('--mm-swatch').trim();
    const accent = styles.getPropertyValue('--mm-col-accent').trim();

    setPreview(panel, {
      title: link.dataset.mmPreview,
      url: href,
      bg: swatch || accent,
      img: link.mmImg || undefined,
    });

    if (link.mmImg === undefined) {
      prepareLink(link, panel).then((url) => {
        if (panel.dataset.mmToken === token) setPreview(panel, { img: url || '' });
      });
    }
  }

  document.addEventListener('pointerover', onEnter, { passive: true });
  document.addEventListener('focusin', onEnter);

  function warmUp() {
    document.querySelectorAll('.mm-menu-preview').forEach((panel) => {
      if (panel instanceof HTMLElement) preparePanel(panel);
    });
  }
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  if (document.readyState === 'complete') idle(warmUp);
  else window.addEventListener('load', () => idle(warmUp));
})();
